/**
 * Mid-track zone transfer — stop old sink, register stream for new consumer,
 * playUri (SetAVTransportURI), then seek when supported.
 */

import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from './TransportCommand';
import { QueueController } from './QueueController';
import { isDsd } from '../utils/formatDetection';
import {
  classifyStreamHostKind,
  isLocalLibraryTrack,
} from '../lib/playback-bridge';
import { getFormatIdForRenderer, isLimitedRenderer } from './types';
import { getClientTransport } from '../handlers/ClientTransportHandler';
import { connectorRendererFromZone } from './gaplessSetNext';
import { detectPlatform } from '../utils/platform';
import type { ZoneRow } from './zoneRestorePick';
import { registerHandoffStreamForNetwork } from './zoneStreamRegistration';

export type ZoneTransferParams = {
  zone: ZoneRow;
  isLocal: boolean;
  currentTrack: any;
  positionSeconds: number;
  oldRenderer: { id: string; name?: string } | null;
  wasExclusive: boolean;
  /** True when ZoneController._teardownOldZone already stopped the old sink. */
  stopAlreadyDone?: boolean;
};

export function rendererSupportsSeek(zone: ZoneRow | null | undefined): boolean {
  if (!zone) return false;
  if (zone.metadata?.supports_seek === false) return false;
  if (zone.capabilities && (zone.capabilities as { supportsSeek?: boolean }).supportsSeek === false) {
    return false;
  }
  const t = zone.type || '';
  return t === 'upnp' || t === 'manual' || t === 'psaudio' || t === 'lua' || t === 'local-native';
}

/**
 * Re-export for tests and external callers.
 */
export { registerHandoffStreamForNetwork } from './zoneStreamRegistration';

async function playLocalLibraryOnNetwork(
  track: any,
  zone: ZoneRow,
): Promise<{ url: string; formatId: number } | null> {
  if (!track?.file_path) return null;
  const { isConnectorAvailable, playLocalFile, networkRendererTransportLabel } = await import('../lib/connector');
  if (!isConnectorAvailable()) return null;

  const az = zone;
  const transportLabel = networkRendererTransportLabel(String(az.metadata?.transport_type || az.type || 'upnp'));
  const playResult = await playLocalFile(
    track.file_path,
    zone.id,
    track.title || '',
    track.artist || '',
    track.album || '',
    Math.round(track.duration || 0),
    track.embedded_cover_hash || track.folder_cover_hash || null,
    track.sample_rate ?? null,
    track.bit_depth ?? null,
    track.channels ?? null,
    track.file_size ?? null,
  );

  if (!playResult.success) {
    throw new Error(playResult.error || `play_local_file failed (${transportLabel})`);
  }

  const proxyUrl = playResult.proxyUrl || '';
  const hostKind = classifyStreamHostKind(proxyUrl);
  if (hostKind === 'loopback') {
    console.warn('[zone-transfer] play_local_file returned loopback URL for network renderer');
  }

  const renderer = connectorRendererFromZone(zone);
  const formatId = getFormatIdForRenderer(renderer);
  const store = useMaestroStore.getState();
  store.setCurrentStreamUrl(proxyUrl);
  store.setCurrentStreamFormatId(formatId);
  store.setRendererHasStream(true);
  store.setPlaybackState('loading');

  return { url: proxyUrl, formatId };
}

/**
 * Mid-track transfer sequence: stop (caller) → register → playUri → seek.
 * DSD: no mid-track transfer — plain restart on the new zone from 0:00.
 */
export async function executeMidTrackZoneTransfer(params: ZoneTransferParams): Promise<void> {
  const {
    zone, isLocal, currentTrack, positionSeconds, oldRenderer, wasExclusive, stopAlreadyDone,
  } = params;
  if (!currentTrack) return;

  const store = useMaestroStore.getState();
  const position = Math.max(0, Math.floor(positionSeconds));

  if (isDsd(currentTrack)) {
    console.log('[zone-transfer] DSD — plain restart on new zone (no mid-track seek)');
    if (isLocal) {
      const url = store.currentStreamUrl || store.alternateStreamUrl;
      if (url) getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
    } else if (isLocalLibraryTrack(currentTrack) && currentTrack.file_path) {
      await playLocalLibraryOnNetwork(currentTrack, zone);
    } else {
      const primaryUrl = store.currentStreamUrl;
      const formatId = store.currentStreamFormatId || 27;
      if (primaryUrl) {
        await TransportCommand.playUri(primaryUrl, zone.id, formatId, currentTrack);
      }
    }
    return;
  }

  if (!stopAlreadyDone) {
    if (wasExclusive) {
      await TransportCommand.stopExclusiveEngine().catch(() => {});
    } else if (oldRenderer?.id) {
      await TransportCommand.stopRenderer(oldRenderer.id).catch(() => {});
    } else {
      TransportCommand.pauseLocalAudioElement();
    }
  }

  if (isLocal) {
    const url = store.currentStreamUrl || store.alternateStreamUrl;
    if (!url) return;
    if (await TransportCommand.shouldUseExclusive()) {
      await TransportCommand.playUri(url, '', store.currentStreamFormatId || 27, currentTrack);
    } else {
      getClientTransport().playLocalStream({ ...currentTrack, streamUrl: url });
    }
    if (position > 1) {
      await TransportCommand.seek(position);
    }
    return;
  }

  if (isLocalLibraryTrack(currentTrack) && currentTrack.file_path && detectPlatform().isTauriDesktop) {
    const played = await playLocalLibraryOnNetwork(currentTrack, zone);
    if (played && position > 1 && rendererSupportsSeek(zone)) {
      await TransportCommand.seek(position);
    } else if (position > 1) {
      console.log('[zone-transfer] seek unsupported after local-file handoff — restarted track');
    }
    return;
  }

  const primaryUrl = store.currentStreamUrl;
  const primaryFmt = store.currentStreamFormatId;
  const altUrl = store.alternateStreamUrl;
  const altFmt = store.alternateStreamFormatId;
  const renderer = connectorRendererFromZone(zone);
  const needsCD = isLimitedRenderer(renderer);

  let url: string | null = primaryUrl;
  let formatId = primaryFmt || 27;
  if (needsCD) {
    if (primaryFmt === 6 && primaryUrl) { url = primaryUrl; formatId = 6; }
    else if (altFmt === 6 && altUrl) { url = altUrl; formatId = 6; }
    else if (primaryUrl) { url = primaryUrl; formatId = primaryFmt || 27; }
  } else {
    if (primaryFmt === 27 && primaryUrl) { url = primaryUrl; formatId = 27; }
    else if (altFmt === 27 && altUrl) { url = altUrl; formatId = 27; }
    else {
      const resolved = await QueueController.resolveStreamUrl(currentTrack, renderer);
      if (resolved) { url = resolved.url; formatId = resolved.formatId || 27; }
    }
  }

  if (!url) {
    console.error('[zone-transfer] no stream URL for handoff');
    return;
  }

  const registered = await registerHandoffStreamForNetwork(url, zone, currentTrack, formatId);
  url = registered.url;
  formatId = registered.formatId;

  if (classifyStreamHostKind(url) === 'loopback') {
    console.warn('[zone-transfer] handoff URL is loopback — network renderer may not reach stream');
  }

  await TransportCommand.playUri(url, zone.id, formatId, currentTrack);

  if (position > 1 && rendererSupportsSeek(zone)) {
    await TransportCommand.seek(position);
  } else if (position > 1) {
    console.log('[zone-transfer] seek unsupported on target renderer — restarted track from beginning');
  }
}
